'use client';

import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import type { LoginOutcome } from '@/shared/lib/auth/two-factor';
import { cn } from '@/shared/utils/formatting';
import type { LoginCredentials } from '@/types';
import { AxiosError } from 'axios';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import Link from 'next/link';
import posthog from 'posthog-js';
import { useState } from 'react';
import { toast } from 'sonner';

interface PasswordFormProps {
  email: string;
  setEmail: (value: string) => void;
  isAnyLoading: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<LoginOutcome>;
  clearError: () => void;
  onSuccess: () => void;
  onTwoFactor: (challengeToken: string) => void;
  onSwitchToOtp: () => void;
}

// Prototype-aligned tokens (temp/sign-in-taboo/Sign In.html).
const INPUT =
  'w-full h-11 bg-transparent border-0 border-b border-[#2a1c22] rounded-none px-0.5 py-3 text-base text-text-primary placeholder:text-text-tertiary outline-none transition-colors hover:border-[#4a2a33] focus:border-red-primary disabled:opacity-70';
const INPUT_ERR = 'border-red-primary';
const PRIMARY =
  'w-full rounded-[2px] bg-red-primary text-white uppercase tracking-[0.1em] text-xs font-bold py-[17px] transition-colors hover:bg-red-hover active:bg-[#a51420] disabled:opacity-55 disabled:cursor-not-allowed flex items-center justify-center gap-2';
const GHOST =
  'self-center border-b border-transparent text-[13px] font-medium text-text-secondary tracking-[0.01em] py-2.5 transition-colors hover:text-white hover:border-white/20 disabled:opacity-70';
const ERR = 'text-[12.5px] text-red-primary pl-2.5 border-l-2 border-red-primary';

/**
 * §8 FRONT-341 shape: stable `error_code` (BE code → `http_<status>` → `network`)
 * so PostHog can split wrong-password 422s from throttles and outages.
 */
function loginFailureProps(err: unknown) {
  if (!(err instanceof AxiosError)) return { error_code: 'unknown' };
  const status = err.response?.status;
  if (status === undefined) return { error_code: 'network' };
  const data = err.response?.data as { code?: unknown; message?: unknown } | undefined;
  const error_code = typeof data?.code === 'string' ? data.code : `http_${status}`;
  return {
    error_code,
    http_status: status,
    ...(typeof data?.message === 'string' ? { error_message: data.message } : {}),
  };
}

export function PasswordForm({
  email,
  setEmail,
  isAnyLoading,
  isLoading,
  login,
  clearError,
  onSuccess,
  onTwoFactor,
  onSwitchToOtp,
}: PasswordFormProps) {
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
    clearError();
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
    clearError();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};
    if (!email) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!password) newErrors.password = 'Password is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const outcome = await login({ email, password });
      if (outcome.twoFactorRequired) {
        onTwoFactor(outcome.challengeToken);
        return;
      }
      onSuccess();
    } catch (err) {
      posthog.capture(AnalyticsEvent.AUTH_LOGIN_FAILED, {
        method: 'email',
        error_type: err instanceof AxiosError ? 'api_error' : 'unknown',
        ...loginFailureProps(err),
      });
      if (err instanceof AxiosError && err.response?.data?.errors) {
        const apiErrors = err.response.data.errors;
        const formattedErrors: Record<string, string> = {};
        Object.keys(apiErrors).forEach((key) => {
          formattedErrors[key] = apiErrors[key][0];
        });
        setErrors(formattedErrors);
      } else {
        toast.error('Invalid email or password');
      }
    }
  };

  return (
    <form method="post" onSubmit={handleSubmit} className="flex flex-col gap-3.5 my-8">
      <div className="flex flex-col gap-1.5">
        <label htmlFor="email" className="text-[12.5px] font-medium text-text-secondary">
          Email
        </label>
        <input
          id="email"
          type="email"
          name="email"
          placeholder="you@example.com"
          value={email}
          onChange={handleEmailChange}
          disabled={isAnyLoading}
          autoComplete="email"
          className={cn(
            `${INPUT} ${errors.email ? INPUT_ERR : ''}`,
            'focus-visible:outline-0! focus-visible:outline-offset-0!'
          )}
        />
        {errors.email && <p className={ERR}>{errors.email}</p>}
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="password" className="text-[12.5px] font-medium text-text-secondary">
          Password
        </label>
        <div className="relative flex">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="••••••••"
            value={password}
            onChange={handlePasswordChange}
            disabled={isAnyLoading}
            autoComplete="current-password"
            className={cn(
              `${INPUT} pr-10 ${errors.password ? INPUT_ERR : ''}`,
              'focus-visible:outline-0! focus-visible:outline-offset-0!'
            )}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-0 top-1/2 -translate-y-1/2 grid h-8 w-8 place-items-center text-text-tertiary transition-colors hover:text-text-primary"
            tabIndex={-1}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <EyeOff className="h-[18px] w-[18px]" />
            ) : (
              <Eye className="h-[18px] w-[18px]" />
            )}
          </button>
        </div>
        {errors.password && <p className={ERR}>{errors.password}</p>}
      </div>

      <Link
        href="/forgot-password"
        className="self-start -mt-1 text-[13px] font-medium text-red-primary transition-colors hover:text-red-hover hover:underline underline-offset-2"
      >
        Forgot password?
      </Link>

      <button type="submit" disabled={isAnyLoading} className={PRIMARY}>
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Signing in
          </>
        ) : (
          'Sign In'
        )}
      </button>

      <button type="button" onClick={onSwitchToOtp} disabled={isAnyLoading} className={GHOST}>
        Send me a code instead
      </button>
    </form>
  );
}
